/* EventoLandingPage.jsx — landing page evento (es. Pizza Park × Colucci)
 * UX mobile-first: hero CTA scroll-to-form, input h-12 + text-base
 * (no zoom iOS), chip 44x44 WCAG, sticky submit bar mobile,
 * FAQ tap-area full-row, inputMode email/tel, contrast WCAG AA.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LegalLinkBubble from "@/components/LegalLink";
import {
  Trophy, MapPin, Calendar, ShieldCheck, Loader2, Sparkles,
  HelpCircle, ChevronDown, ArrowDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/eventi`;

export default function EventoLandingPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    nome: "", cognome: "", email: "", telefono: "",
    interesse_prodotti: [], note: "",
    residenza_comune: "", ultimo_premio_pagato: "",
    // v1.14.46 — Data scadenza polizza attuale (ISO yyyy-mm-dd, opzionale)
    data_scadenza_polizza: "",
    // v1.14.39 — sub-tipo "Professioni sanitarie"
    professioni_sanitarie_tipo: null,
    // v1.14.42 — RC Professionale Sanitaria (legacy v1, kept per backend compat)
    rc_prof_ssn_tipo: null,            // "ssn_no_extra" | "ssn_con_extra" | null
    // v1.14.49 — Schema v2: 3 ruoli SSN + extramoenia solo per dirigente
    rc_prof_ssn_ruolo: null,           // "specializzando" | "quadro_sanitario" | "dirigente_medico"
    rc_prof_ssn_extramoenia: false,    // bool (significativo solo per dirigente_medico)
    rc_prof_specializzazione: null,    // codice specializzazione libero pro
    privacy: false, marketing: false, consenso_concorso: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // v1.14.42 — specializzazioni Libero Professionista (popolate da BE,
  // vuote finché il CSV non viene caricato)
  const [rcProfSpec, setRcProfSpec] = useState({
    items: [], available: false, filtered: false, message: null, loading: true,
  });
  const formRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/${slug}`)
      .then((r) => setEv(r.data))
      .catch(() => toast.error("Evento non disponibile"))
      .finally(() => setLoading(false));
  }, [slug]);

  // v1.14.42 — fetch specializzazioni RC Prof Libero Pro (lazy)
  // v1.14.44 — usa endpoint EVENT-SCOPED per filtrare le specializzazioni
  // pertinenti al singolo evento (es. SOTIMI → 6 ortopediche).
  useEffect(() => {
    if (form.professioni_sanitarie_tipo !== "libero_professionista") return;
    if (!rcProfSpec.loading && rcProfSpec.items.length === 0 && rcProfSpec.available) return;
    axios.get(`${API}/${slug}/rc-prof/specializzazioni`)
      .then((r) => {
        const items = r.data?.specializzazioni || [];
        setRcProfSpec({
          items,
          available: !!r.data?.tariffario_disponibile,
          filtered: !!r.data?.filtrato_per_evento,
          message: r.data?.messaggio_no_dati || null,
          loading: false,
        });
        // Se l'evento ha esattamente 1 specializzazione ammessa → preseleziona
        if (items.length === 1) {
          setForm((f) => ({
            ...f,
            rc_prof_specializzazione: items[0].codice,
          }));
        }
      })
      .catch(() => setRcProfSpec({
        items: [], available: false, filtered: false,
        message: "Tariffario in aggiornamento — chiedi consulenza diretta in fiera",
        loading: false,
      }));
  }, [form.professioni_sanitarie_tipo, slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleProd = (p) => {
    setForm((f) => {
      const has = f.interesse_prodotti.includes(p);
      const next = {
        ...f,
        interesse_prodotti: has
          ? f.interesse_prodotti.filter((x) => x !== p)
          : [...f.interesse_prodotti, p],
      };
      // v1.14.39 — se l'utente deseleziona "Professioni sanitarie",
      // resetta il sub-tipo
      // v1.14.42 — resetta anche i campi RC Professionale Sanitaria
      if (p === "Professioni sanitarie" && has) {
        next.professioni_sanitarie_tipo = null;
        next.rc_prof_ssn_tipo = null;
        next.rc_prof_ssn_ruolo = null;
        next.rc_prof_ssn_extramoenia = false;
        next.rc_prof_specializzazione = null;
      }
      return next;
    });
    setErrors((e) => ({
      ...e, interesse_prodotti: undefined,
      professioni_sanitarie_tipo: undefined,
    }));
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      document.querySelector("[data-testid='evento-field-nome']")?.focus();
    }, 600);
  };

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = "Nome obbligatorio";
    if (!form.cognome.trim()) e.cognome = "Cognome obbligatorio";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Email non valida";
    if (!form.telefono.trim() || form.telefono.replace(/\D/g, "").length < 9)
      e.telefono = "Telefono non valido";
    if (form.interesse_prodotti.length === 0)
      e.interesse_prodotti = "Seleziona almeno un prodotto";
    if (!form.privacy) e.privacy = "Devi accettare la privacy";
    // COMPLIANCE: rimosso vincolo "consenso_concorso" (era opzionale anche prima)
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      toast.error("Controlla i campi evidenziati");
      const firstErrField = Object.keys(errors)[0] || "nome";
      document.querySelector(`[data-testid='evento-field-${firstErrField}']`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/${slug}/iscrivi`, {
        ...form,
        // v1.14.36 — campi opzionali estesi
        residenza_comune: form.residenza_comune?.trim() || null,
        ultimo_premio_pagato: form.ultimo_premio_pagato !== ""
          ? Number(form.ultimo_premio_pagato) : null,
        // v1.14.46 — data scadenza polizza (ISO yyyy-mm-dd o null)
        data_scadenza_polizza: form.data_scadenza_polizza?.trim()
          ? form.data_scadenza_polizza : null,
        // v1.14.39 — invia il sub-tipo solo se Professioni sanitarie è selezionato
        professioni_sanitarie_tipo:
          form.interesse_prodotti.includes("Professioni sanitarie")
            ? form.professioni_sanitarie_tipo : null,
        // v1.14.42 — RC Professionale Sanitaria (legacy field kept for BC)
        rc_prof_ssn_tipo:
          form.interesse_prodotti.includes("Professioni sanitarie")
            && form.professioni_sanitarie_tipo === "pubblico"
            ? form.rc_prof_ssn_tipo : null,
        // v1.14.49 — schema v2 (3 ruoli + extramoenia)
        rc_prof_ssn_ruolo:
          form.interesse_prodotti.includes("Professioni sanitarie")
            && form.professioni_sanitarie_tipo === "pubblico"
            ? form.rc_prof_ssn_ruolo : null,
        rc_prof_ssn_extramoenia:
          form.interesse_prodotti.includes("Professioni sanitarie")
            && form.professioni_sanitarie_tipo === "pubblico"
            && form.rc_prof_ssn_ruolo === "dirigente_medico"
            ? !!form.rc_prof_ssn_extramoenia : null,
        rc_prof_specializzazione:
          form.interesse_prodotti.includes("Professioni sanitarie")
            && form.professioni_sanitarie_tipo === "libero_professionista"
            ? form.rc_prof_specializzazione : null,
        // COMPLIANCE DPR 430/2001: rimosso consenso_concorso, usiamo consenso_promo
        consenso_promo: form.consenso_concorso || false,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
      });
      nav(`/eventi/${slug}/conferma`, { state: data });
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
                   || "Errore iscrizione, riprova");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#163321]" />
      </div>
    );
  }
  if (!ev) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-700">Evento non disponibile</h1>
            <p className="text-slate-500 mt-2">L'evento richiesto è terminato o non esiste.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const bgGradient = ev.banner_color
    ? `linear-gradient(135deg, ${ev.banner_color} 0%, #163321 100%)`
    : "linear-gradient(135deg, #D97B58 0%, #163321 100%)";

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <main className="flex-1 pb-24 md:pb-0">
        {/* HERO */}
        <section
          data-testid="evento-hero"
          className="relative overflow-hidden text-white py-12 sm:py-16 md:py-24"
          style={{ background: bgGradient }}
        >
          <div className="absolute top-0 left-0 w-72 h-72 rounded-full bg-white/10 blur-3xl -translate-x-32 -translate-y-32" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-amber-300/20 blur-3xl translate-x-32 translate-y-32" />
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-bold mb-4">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> Evento in corso
            </div>
            <div className="text-5xl sm:text-6xl md:text-7xl mb-3" aria-hidden="true">🍕🛡️</div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-3 drop-shadow">
              {ev.titolo}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/95 max-w-2xl mx-auto leading-relaxed">
              {ev.sottotitolo}
            </p>
            {ev.incentivo && (
              <div className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-400 text-[#163321] font-bold text-sm sm:text-base shadow-lg">
                <Trophy className="w-5 h-5" aria-hidden="true" />
                {ev.incentivo}
              </div>
            )}
            {/* CTA hero: scroll-to-form */}
            <div className="mt-7">
              <button
                type="button"
                onClick={scrollToForm}
                data-testid="evento-hero-cta-scroll"
                className="inline-flex items-center justify-center gap-2 min-h-[56px] px-8 py-4 rounded-full bg-white text-[#163321] font-black text-base sm:text-lg shadow-2xl hover:scale-[1.02] active:scale-[0.98] transition-transform focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber-300"
              >
                Iscriviti all'evento
                <ArrowDown className="w-5 h-5 animate-bounce" aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        {/* INFO + FORM in grid */}
        <section className="max-w-5xl mx-auto px-4 md:px-6 py-10 sm:py-12 md:py-16 grid md:grid-cols-2 gap-6 md:gap-8">
          {/* Info evento */}
          <div className="space-y-5 order-2 md:order-1">
            {(ev.luogo || ev.data_inizio) && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <h3 className="font-bold text-[#163321] mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5" aria-hidden="true" /> Dove e quando
                </h3>
                {ev.luogo && (
                  <p className="text-sm text-slate-700 flex items-start gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" aria-hidden="true" />
                    {ev.luogo}
                  </p>
                )}
                {ev.data_inizio && (
                  <p className="text-sm text-slate-700 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
                    Dal {ev.data_inizio}{ev.data_fine && ` al ${ev.data_fine}`}
                  </p>
                )}
              </div>
            )}
            {ev.descrizione_html && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm prose prose-sm max-w-none text-slate-700"
                    dangerouslySetInnerHTML={{ __html: ev.descrizione_html }} />
            )}
            {/* FAQ */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-bold text-[#163321] mb-3 flex items-center gap-2">
                <HelpCircle className="w-5 h-5" aria-hidden="true" /> Domande frequenti
              </h3>
              <Faq q="Cos'è questo evento?" a={
                ev.organizzatore
                  ? `${ev.titolo}: evento organizzato da ${ev.organizzatore} con la partecipazione di Colucci Assicuratori. Ai partecipanti che si presentano allo stand offriamo una consulenza assicurativa gratuita personalizzata${ev.incentivo ? ` e ${ev.incentivo.toLowerCase()}` : ""}.`
                  : `${ev.titolo}: passa al nostro stand per una consulenza assicurativa gratuita personalizzata${ev.incentivo ? ` e ${ev.incentivo.toLowerCase()}` : ""}.`
              } />
              <Faq q="Cosa ricevo se vengo?" a={
                ev.incentivo
                  ? `${ev.incentivo} + consulenza gratuita su misura del tuo profilo familiare o aziendale, senza impegno e senza obbligo di acquisto.`
                  : "Una consulenza gratuita su misura del tuo profilo familiare o aziendale, senza impegno e senza obbligo di acquisto."
              } />
              <Faq q="È un concorso a premi?" a="No. Si tratta di un'iniziativa di marketing promozionale, non un concorso a premi regolamentato dal DPR 430/2001. Nessuna estrazione, nessuna classifica: eventuali omaggi/gadget vengono distribuiti fino ad esaurimento scorte." />
              <Faq q="I miei dati sono al sicuro?" a="Sì. Trattati ex GDPR, conservati in Italia/UE, mai venduti a terzi. Cancellazione gratuita on-demand." />
            </div>
          </div>

          {/* FORM */}
          <div className="order-1 md:order-2" ref={formRef}>
            <div
              className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 md:p-7 shadow-xl md:sticky md:top-4"
              data-testid="evento-form"
            >
              <h2 className="text-2xl sm:text-3xl font-black text-[#163321] mb-1">
                Iscriviti in 30 secondi
              </h2>
              <p className="text-sm text-slate-500 mb-5">
                Ti contattiamo entro 48h per la consulenza gratuita.
              </p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Nome *" tid="nome" value={form.nome}
                    autoComplete="given-name"
                    error={errors.nome}
                    onChange={(v) => { setForm({ ...form, nome: v }); setErrors((e) => ({ ...e, nome: undefined })); }} />
                  <Field label="Cognome *" tid="cognome" value={form.cognome}
                    autoComplete="family-name"
                    error={errors.cognome}
                    onChange={(v) => { setForm({ ...form, cognome: v }); setErrors((e) => ({ ...e, cognome: undefined })); }} />
                </div>
                <Field label="Email *" tid="email" type="email" value={form.email}
                  inputMode="email" autoComplete="email"
                  placeholder="tua@email.it"
                  error={errors.email}
                  onChange={(v) => { setForm({ ...form, email: v }); setErrors((e) => ({ ...e, email: undefined })); }} />
                <Field label="Telefono *" tid="telefono" type="tel"
                  inputMode="tel" autoComplete="tel"
                  value={form.telefono}
                  placeholder="+39 333 1234567"
                  error={errors.telefono}
                  onChange={(v) => { setForm({ ...form, telefono: v }); setErrors((e) => ({ ...e, telefono: undefined })); }} />

                {/* Chip prodotti — 44x44 WCAG */}
                <div>
                  <Label className="text-sm font-bold text-slate-800 mb-2 block">
                    Cosa ti interessa? *
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      // v1.14.36 — garantisce "Professioni" anche se evento DB
                      // non l'ha ancora propagato (event-loop ensure_indexes
                      // gira al boot, ma idempotente).
                      // v1.14.39 — garantisce anche "Professioni sanitarie"
                      const base = ev.prodotti_interesse_disponibili || [];
                      const needed = ["Professioni", "Professioni sanitarie"];
                      const list = [
                        ...base.filter((x) => x !== "Altro" && !needed.includes(x)),
                        ...needed.filter((n) => !base.includes(n) || base.includes(n)),
                        ...(base.includes("Altro") ? ["Altro"] : []),
                      ].filter((x, i, arr) => arr.indexOf(x) === i);
                      // Conserva ordine canonico mantenendo gli esistenti
                      const ordered = [];
                      for (const x of base) {
                        if (x !== "Altro" && !ordered.includes(x)) ordered.push(x);
                      }
                      for (const n of needed) if (!ordered.includes(n)) ordered.push(n);
                      if (base.includes("Altro")) ordered.push("Altro");
                      return ordered.length ? ordered : list;
                    })().map((p) => {
                      const active = form.interesse_prodotti.includes(p);
                      return (
                        <button
                          type="button" key={p}
                          data-testid={`evento-prod-${p.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => toggleProd(p)}
                          aria-pressed={active}
                          className={[
                            "min-h-[44px] px-4 py-2.5 rounded-full text-sm font-bold border-2 transition-all active:scale-95",
                            active
                              ? "bg-[#163321] text-white border-[#163321] shadow-md"
                              : "bg-white text-slate-700 border-slate-300 hover:border-[#163321] hover:text-[#163321]",
                          ].join(" ")}
                        >
                          {active ? "✓ " : ""}{p}
                        </button>
                      );
                    })}
                  </div>
                  {errors.interesse_prodotti && (
                    <p className="text-rose-600 text-xs font-medium mt-1.5 flex items-center gap-1">
                      ⚠️ {errors.interesse_prodotti}
                    </p>
                  )}
                </div>

                {/* v1.14.39 — Sub-tipo condizionale per "Professioni sanitarie" */}
                {form.interesse_prodotti.includes("Professioni sanitarie") && (
                  <div
                    data-testid="evento-prof-sanitarie-subtype"
                    className="rounded-xl border-2 border-[#163321]/30 bg-emerald-50/40 p-4 space-y-3"
                  >
                    <Label className="text-sm font-bold text-slate-800 block">
                      Tipologia di professione sanitaria *
                    </Label>
                    <div className="flex flex-wrap gap-2" role="radiogroup"
                         aria-label="Tipo di professione sanitaria">
                      {[
                        { value: "pubblico", label: "SSN" },
                        { value: "libero_professionista", label: "Libero professionista" },
                      ].map((opt) => {
                        const active = form.professioni_sanitarie_tipo === opt.value;
                        return (
                          <button
                            type="button" key={opt.value}
                            data-testid={`evento-prof-sanitarie-${opt.value}`}
                            role="radio"
                            aria-checked={active}
                            onClick={() => {
                              setForm({
                                ...form,
                                professioni_sanitarie_tipo: opt.value,
                                // v1.14.42 / v1.14.49 — reset campi RC Prof
                                rc_prof_ssn_tipo: null,
                                rc_prof_ssn_ruolo: null,
                                rc_prof_ssn_extramoenia: false,
                                rc_prof_specializzazione: null,
                              });
                              setErrors((e) => ({ ...e, professioni_sanitarie_tipo: undefined }));
                            }}
                            className={[
                              "min-h-[44px] px-5 py-2.5 rounded-full text-sm font-bold border-2 transition-all active:scale-95",
                              active
                                ? "bg-[#163321] text-white border-[#163321] shadow-md"
                                : "bg-white text-slate-700 border-slate-300 hover:border-[#163321] hover:text-[#163321]",
                            ].join(" ")}
                          >
                            {active ? "✓ " : ""}{opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {errors.professioni_sanitarie_tipo && (
                      <p className="text-rose-600 text-xs font-medium flex items-center gap-1">
                        ⚠️ {errors.professioni_sanitarie_tipo}
                      </p>
                    )}

                    {/* v1.14.49 — Cascade SSN: 3 ruoli + extramoenia per dirigente */}
                    {form.professioni_sanitarie_tipo === "pubblico" && (
                      <div
                        data-testid="evento-rc-prof-ssn-cascade"
                        className="mt-3 pt-3 border-t border-emerald-200/60 space-y-3"
                      >
                        <div>
                          <Label className="text-sm font-bold text-slate-800 block mb-1.5">
                            Ruolo SSN
                          </Label>
                          <div className="flex flex-wrap gap-2" role="radiogroup"
                                aria-label="Ruolo SSN">
                            {[
                              { value: "specializzando", label: "Specializzando" },
                              { value: "quadro_sanitario", label: "Quadro Sanitario" },
                              { value: "dirigente_medico",
                                label: "Dirigente Medico / Medico Non Dirigente" },
                            ].map((opt) => {
                              const active = form.rc_prof_ssn_ruolo === opt.value;
                              return (
                                <button
                                  type="button" key={opt.value}
                                  data-testid={`evento-rc-prof-ssn-ruolo-${opt.value}`}
                                  role="radio" aria-checked={active}
                                  onClick={() => {
                                    setForm({
                                      ...form,
                                      rc_prof_ssn_ruolo: opt.value,
                                      // Reset extramoenia se non dirigente
                                      rc_prof_ssn_extramoenia:
                                        opt.value === "dirigente_medico"
                                          ? form.rc_prof_ssn_extramoenia : false,
                                    });
                                  }}
                                  className={[
                                    "min-h-[44px] px-4 py-2.5 rounded-full text-sm font-bold border-2 transition-all active:scale-95",
                                    active
                                      ? "bg-[#163321] text-white border-[#163321] shadow-md"
                                      : "bg-white text-slate-700 border-slate-300 hover:border-[#163321] hover:text-[#163321]",
                                  ].join(" ")}
                                >
                                  {active ? "✓ " : ""}{opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Sub-toggle extramoenia visibile solo per Dirigente */}
                        {form.rc_prof_ssn_ruolo === "dirigente_medico" && (
                          <div data-testid="evento-rc-prof-extramoenia-toggle">
                            <Label className="text-sm font-bold text-slate-800 block mb-1.5">
                              Extramoenia
                            </Label>
                            <div className="flex flex-wrap gap-2" role="radiogroup"
                                  aria-label="Extramoenia">
                              {[
                                { value: false, label: "No" },
                                { value: true, label: "Sì" },
                              ].map((opt) => {
                                const active = !!form.rc_prof_ssn_extramoenia === opt.value;
                                return (
                                  <button
                                    type="button" key={String(opt.value)}
                                    data-testid={`evento-rc-prof-extramoenia-${opt.value ? 'si' : 'no'}`}
                                    role="radio" aria-checked={active}
                                    onClick={() => setForm({
                                      ...form, rc_prof_ssn_extramoenia: opt.value,
                                    })}
                                    className={[
                                      "min-h-[44px] px-5 py-2.5 rounded-full text-sm font-bold border-2 transition-all active:scale-95",
                                      active
                                        ? "bg-[#163321] text-white border-[#163321] shadow-md"
                                        : "bg-white text-slate-700 border-slate-300 hover:border-[#163321] hover:text-[#163321]",
                                    ].join(" ")}
                                  >
                                    {active ? "✓ " : ""}{opt.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* v1.14.42 — Cascade Libero Professionista: dropdown specializzazione */}
                    {/* v1.14.44 — Se l'evento ha 1 sola spec ammessa → read-only preselezionata */}
                    {form.professioni_sanitarie_tipo === "libero_professionista" && (
                      <div
                        data-testid="evento-rc-prof-libero-cascade"
                        className="mt-3 pt-3 border-t border-emerald-200/60 space-y-2"
                      >
                        <Label htmlFor="evento-rc-prof-spec"
                          className="text-sm font-bold text-slate-800 block">
                          Specializzazione
                        </Label>
                        {/* Caso A: 1 sola spec ammessa → read-only card preselezionata */}
                        {rcProfSpec.items.length === 1 && rcProfSpec.filtered ? (
                          <div
                            data-testid="evento-rc-prof-specializzazione-readonly"
                            className="rounded-md border-2 border-emerald-300 bg-emerald-50/60 px-3 py-3 flex items-center justify-between gap-3"
                          >
                            <div>
                              <div className="font-bold text-emerald-900 text-sm">
                                {rcProfSpec.items[0].nome}
                              </div>
                              {rcProfSpec.items[0].categoria_label && (
                                <div className="text-[11px] text-emerald-800/80 mt-0.5">
                                  Categoria: {rcProfSpec.items[0].categoria_label}
                                </div>
                              )}
                            </div>
                            <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-1 rounded bg-emerald-200/80 text-emerald-900">
                              Pre-selezionata
                            </span>
                          </div>
                        ) : (
                          <>
                            <select
                              id="evento-rc-prof-spec"
                              data-testid="evento-rc-prof-specializzazione-select"
                              disabled={!rcProfSpec.available}
                              value={form.rc_prof_specializzazione || ""}
                              onChange={(e) => setForm({
                                ...form, rc_prof_specializzazione: e.target.value || null,
                              })}
                              className={[
                                "w-full h-12 px-3 rounded-md border-2 text-base bg-white",
                                !rcProfSpec.available
                                  ? "border-amber-300 text-slate-400 cursor-not-allowed bg-amber-50/40"
                                  : "border-slate-300 text-slate-900",
                              ].join(" ")}
                            >
                              <option value="">
                                {rcProfSpec.loading
                                  ? "Caricamento…"
                                  : (rcProfSpec.available
                                      ? "— Seleziona specializzazione —"
                                      : "— non disponibile —")}
                              </option>
                              {rcProfSpec.items.map((s) => (
                                <option key={s.codice} value={s.codice}>
                                  {s.nome}{s.categoria_label ? ` — ${s.categoria_label}` : ""}
                                </option>
                              ))}
                            </select>
                            {rcProfSpec.filtered && rcProfSpec.items.length > 1 && (
                              <p className="text-[11px] text-emerald-800/80"
                                  data-testid="evento-rc-prof-filter-hint">
                                Lista ristretta alle specializzazioni pertinenti all'evento
                              </p>
                            )}
                          </>
                        )}
                        {!rcProfSpec.available && !rcProfSpec.loading && (
                          <div
                            data-testid="evento-rc-prof-libero-badge"
                            className="inline-flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-100 border border-amber-300 text-amber-900 text-xs font-medium leading-snug"
                          >
                            <span className="text-base leading-none">⚠️</span>
                            <span>
                              <b>Tariffario in aggiornamento</b> — chiedi consulenza diretta in fiera
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* v1.14.36 — Residenza comune (opzionale) */}
                <div>
                  <Label htmlFor="evento-residenza"
                    className="text-sm font-bold text-slate-800 mb-1.5 block">
                    Residenza (solo il comune)
                  </Label>
                  <Input
                    id="evento-residenza"
                    data-testid="evento-field-residenza_comune"
                    type="text"
                    autoComplete="address-level2"
                    value={form.residenza_comune}
                    onChange={(e) => setForm({ ...form, residenza_comune: e.target.value })}
                    placeholder="Comune di residenza"
                    maxLength={100}
                    className="h-12 text-base"
                  />
                </div>

                {/* v1.14.36 — Ultimo premio pagato (opzionale, numerico) */}
                <div>
                  <Label htmlFor="evento-premio"
                    className="text-sm font-bold text-slate-800 mb-1.5 block">
                    Ultimo premio pagato (€)
                  </Label>
                  <Input
                    id="evento-premio"
                    data-testid="evento-field-ultimo_premio_pagato"
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.01"
                    value={form.ultimo_premio_pagato}
                    onChange={(e) => setForm({ ...form, ultimo_premio_pagato: e.target.value })}
                    placeholder="Es. 850"
                    className="h-12 text-base"
                  />
                </div>

                {/* v1.14.46 — Data scadenza polizza (opzionale, date) */}
                <div>
                  <Label htmlFor="evento-scadenza"
                    className="text-sm font-bold text-slate-800 mb-1.5 block">
                    Data scadenza polizza
                  </Label>
                  <Input
                    id="evento-scadenza"
                    data-testid="evento-field-data_scadenza_polizza"
                    type="date"
                    min={(() => {
                      const d = new Date(); d.setDate(d.getDate() - 30);
                      return d.toISOString().slice(0, 10);
                    })()}
                    max={(() => {
                      const d = new Date(); d.setMonth(d.getMonth() + 24);
                      return d.toISOString().slice(0, 10);
                    })()}
                    value={form.data_scadenza_polizza}
                    onChange={(e) => setForm({ ...form, data_scadenza_polizza: e.target.value })}
                    className="h-12 text-base"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Quando scade la tua attuale polizza? (opzionale)
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-bold text-slate-800 mb-1.5 block">
                    Note (opzionale)
                  </Label>
                  <Textarea
                    rows={2} value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Es. polizza in scadenza, preferenza orari..."
                    className="text-base min-h-[80px]"
                  />
                </div>

                <Consent k="privacy" required state={form} setState={setForm}
                  error={errors.privacy}
                  onClear={() => setErrors((e) => ({ ...e, privacy: undefined }))}
                  label={<>Ho letto l'<LegalLink slug="privacy">informativa privacy</LegalLink> e acconsento al trattamento dei dati personali</>} />
                <Consent k="consenso_concorso" state={form} setState={setForm}
                  label={<>Acconsento alle <LegalLink slug="promo-eventi">comunicazioni promo eventi</LegalLink> di Colucci Assicuratori (opzionale)</>} />
                <Consent k="marketing" state={form} setState={setForm}
                  label={<>Voglio ricevere <LegalLink slug="marketing">comunicazioni marketing</LegalLink> (opzionale)</>} />
              </div>

              {/* Submit principale (desktop + mobile in-form) */}
              <Button
                onClick={submit} disabled={submitting}
                className="w-full mt-6 min-h-[60px] text-lg font-black bg-[#163321] hover:bg-[#0e1f15] active:scale-[0.98] transition-transform shadow-lg hidden md:flex"
                data-testid="evento-submit-btn"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" /> Iscrizione in corso...
                  </span>
                ) : (
                  <>Iscrivimi all'evento →</>
                )}
              </Button>

              <p className="text-[11px] text-slate-500 text-center mt-3 flex items-center justify-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
                Dati cifrati AES-256-GCM · Hosting UE
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* STICKY SUBMIT BAR mobile — sempre visibile durante compilazione */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-slate-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]"
        data-testid="evento-sticky-submit"
      >
        <Button
          onClick={submit} disabled={submitting}
          className="w-full min-h-[56px] text-base font-black bg-[#163321] hover:bg-[#0e1f15] active:scale-[0.98] transition-transform shadow-lg"
          data-testid="evento-submit-btn-mobile"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Invio in corso...
            </span>
          ) : (
            <>Iscrivimi all'evento →</>
          )}
        </Button>
      </div>

      {/* COMPLIANCE DPR 430/2001 — disclaimer obbligatorio iniziativa gadget */}
      <ComplianceFooter />

      <Footer />
    </div>
  );
}

function ComplianceFooter() {
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid="evento-compliance-footer"
      className="bg-slate-100 border-t border-slate-200 px-4 py-5 mb-20 md:mb-0"
    >
      <div className="max-w-4xl mx-auto">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          data-testid="evento-compliance-toggle"
          className="w-full flex items-center justify-between gap-2 text-xs sm:text-sm font-bold text-slate-600 hover:text-slate-800 min-h-[44px]"
        >
          <span className="inline-flex items-center gap-2">
            ℹ️ Informazioni legali sull'iniziativa promozionale
          </span>
          <span className={`transition-transform ${open ? "rotate-180" : ""}`}>▼</span>
        </button>
        {open && (
          <p
            data-testid="evento-compliance-disclaimer"
            className="text-xs text-slate-600 leading-relaxed mt-3 pl-1 pr-1"
          >
            Questa iniziativa promozionale prevede l'omaggio di gadget aziendali
            Colucci (penna, cappellino, calamita, borsa promozionale) ai
            partecipanti che si presentano allo stand fino ad esaurimento scorte.{" "}
            <b>NON costituisce concorso a premi</b> ai sensi del{" "}
            <b>DPR 26 ottobre 2001 n. 430</b>, né operazione a premi soggetta ad
            autorizzazione ministeriale. I gadget sono di valore commerciale
            simbolico e vengono distribuiti senza estrazione, sorteggio né
            classifica. La consulenza assicurativa offerta in occasione
            dell'evento è gratuita e senza impegno di acquisto.
          </p>
        )}
      </div>
    </section>
  );
}

function Field({
  label, tid, value, onChange, type = "text", placeholder, error,
  inputMode, autoComplete,
}) {
  return (
    <div>
      <Label
        htmlFor={`evento-field-${tid}`}
        className="text-sm font-bold text-slate-800 mb-1.5 block"
      >
        {label}
      </Label>
      <Input
        id={`evento-field-${tid}`}
        data-testid={`evento-field-${tid}`}
        type={type} value={value}
        inputMode={inputMode}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? `evento-err-${tid}` : undefined}
        className={[
          "h-12 text-base", // h-12 = 48px, text-base = 16px (no zoom iOS)
          error ? "border-rose-500 border-2 focus-visible:ring-rose-400"
                : "border-slate-300 border-2 focus-visible:ring-[#163321]",
        ].join(" ")}
      />
      {error && (
        <p
          id={`evento-err-${tid}`}
          className="text-rose-600 text-xs font-medium mt-1 flex items-center gap-1"
          data-testid={`evento-err-${tid}`}
        >
          ⚠️ {error}
        </p>
      )}
    </div>
  );
}

function Consent({ k, label, required, state, setState, error, onClear }) {
  return (
    <div>
      <label
        data-testid={`evento-consent-${k}`}
        className={[
          "flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer text-sm transition-colors min-h-[56px]",
          error ? "border-rose-400 bg-rose-50"
                : state[k] ? "border-emerald-400 bg-emerald-50"
                            : "border-slate-200 hover:border-slate-400 bg-white",
        ].join(" ")}
      >
        <Checkbox
          checked={state[k]} className="mt-0.5 w-5 h-5"
          onCheckedChange={(c) => {
            setState((s) => ({ ...s, [k]: !!c }));
            onClear && onClear();
          }}
        />
        <span className="text-slate-700 leading-snug select-none">
          {label} {required && <span className="text-rose-500 font-bold">*</span>}
        </span>
      </label>
      {error && (
        <p className="text-rose-600 text-xs font-medium mt-1 ml-1 flex items-center gap-1">
          ⚠️ {error}
        </p>
      )}
    </div>
  );
}

function LegalLink({ slug, children }) {
  return <LegalLinkBubble slug={slug}>{children}</LegalLinkBubble>;
}

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full min-h-[48px] py-3 flex items-center justify-between gap-3 text-left text-sm font-bold text-slate-800 hover:text-[#163321] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#163321] rounded"
      >
        <span className="flex-1">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <p className="text-sm text-slate-600 pb-3 pr-7 leading-relaxed">{a}</p>
      )}
    </div>
  );
}
